import React, { useState, useEffect } from 'react';
import { Search, Code, Globe, AlertCircle, Sparkles } from 'lucide-react';
import { t } from '../services/i18n';
import { LoadingProgressBar } from './LoadingProgressBar';

interface InputFormProps {
  onAnalyze: (url: string, html: string) => void;
  isLoading: boolean;
  loadingMessage?: string;
}

export const InputForm: React.FC<InputFormProps> = ({ onAnalyze, isLoading, loadingMessage }) => {
  const [url, setUrl] = useState('');
  const [html, setHtml] = useState('');
  const [activeTab, setActiveTab] = useState<'url' | 'source'>('url');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isLoading) {
      // Simulate progress (since we don't have real progress from backend)
      setProgress(0);
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) return prev; // Stop at 90% until done
          return prev + Math.random() * 15;
        });
      }, 500);
      return () => clearInterval(interval);
    } else {
      setProgress(100);
      setTimeout(() => setProgress(0), 1000);
    }
  }, [isLoading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAnalyze(url, activeTab === 'source' ? html : '');
  };

  if (isLoading) {
    return (
      <div className="w-full">
        <LoadingProgressBar 
          message={loadingMessage || t('audit.loading') || 'Scanning website...'} 
          progress={progress}
          showProgress={true}
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-3xl mx-auto glass-card rounded-2xl shadow-2xl overflow-hidden text-right perspective-1000" dir="ltr">
      <div className="p-1 bg-slate-100/50 backdrop-blur-sm border-b border-white/20 flex flex-row">
        <button
          type="button"
          onClick={() => setActiveTab('url')}
          className={`flex-1 py-4 text-sm font-semibold flex items-center justify-center gap-2 rounded-t-xl transition-all ${
            activeTab === 'url' 
            ? 'bg-white shadow-lg text-indigo-600 translate-y-0.5' 
            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
          }`}
        >
          <Globe size={18} />
          {t('audit.scanDirect') || 'Scan via URL'}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('source')}
          className={`flex-1 py-4 text-sm font-semibold flex items-center justify-center gap-2 rounded-t-xl transition-all ${
            activeTab === 'source' 
            ? 'bg-white shadow-lg text-indigo-600 translate-y-0.5' 
            : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
          }`}
        >
          <Code size={18} />
          {t('audit.codeSourceTab') || 'Paste HTML Source'}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-8 md:p-10 bg-white/60">
        <div className="mb-8 text-left">
          <label htmlFor="url" className="block text-sm font-bold text-slate-700 mb-3">
            {t('audit.urlLabel') || 'Website URL'}
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-transform group-focus-within:scale-110">
              <Globe className="h-5 w-5 text-indigo-400" />
            </div>
            <input
              type="url"
              id="url"
              required
              dir="ltr"
              className="block w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all shadow-sm text-left text-slate-800 font-medium"
              placeholder={t('audit.urlPlaceholder') || 'https://your-startup.com'}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
        </div>

        {activeTab === 'source' && (
          <div className="mb-8 animate-fadeIn text-left">
            <label htmlFor="html" className="block text-sm font-bold text-slate-700 mb-3">
              {t('audit.htmlLabel') || 'HTML Source (paste here)'}
            </label>
            <div className="relative">
              <textarea
                id="html"
                rows={8}
                dir="ltr"
                className="block w-full p-4 bg-slate-900 text-slate-300 border border-slate-700 rounded-xl font-mono text-xs focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-left shadow-inner"
                placeholder={t('audit.htmlPlaceholder') || '<!-- Paste full HTML source here for a deep audit -->'}
                value={html}
                onChange={(e) => setHtml(e.target.value)}
              />
              <div className="absolute top-3 right-3">
                <Code className="text-slate-600 w-5 h-5" />
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-500 flex items-center gap-1 justify-start font-medium">
              <AlertCircle size={12} className="text-indigo-500" />
              {t('audit.htmlHelp') || 'Copy the full HTML source (Ctrl+U) for the most accurate audit.'}
            </p>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || !url}
          className={`w-full flex items-center justify-center py-4 px-6 rounded-xl text-base font-bold text-white transition-all transform duration-200 ${
            isLoading 
            ? 'bg-slate-400 cursor-not-allowed' 
            : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-1'
          }`}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
              {loadingMessage || t('audit.loading') || 'Scanning site...'}
            </>
          ) : (
            <>
              {activeTab === 'url'
                ? (t('audit.submitUrl') || 'Run Audit')
                : (t('audit.submitHtml') || 'Analyze Source')}
              <Sparkles className="ml-2 h-5 w-5 animate-pulse" />
            </>
          )}
        </button>
      </form>
    </div>
  );
};